import React, { useEffect, useState } from 'react';
import Modal from '../../components/Modal';
import {
    exportSkus,
    ExportSkusPayload,
    listSkus,
    SKU_QR_SIZE_OPTIONS,
    SkuQrNameSource,
    SkuQrSize,
    SkuKind,
} from '../../api/material';
import { ApiError } from '../../api/request';
import { useCapTypes } from '../../components/CapTypeBadges';
import { useCategoryTree } from '../../components/MaterialFieldInputs';
import { dateSuffix } from '../../utils/exportCsv';
import { SkuPropFilter } from '../../components/SkuProp';
import SearchSelect from '../../components/SearchSelect';
import { zhOptions } from '../../utils/searchOptions';
import { SKU_HAS_MATERIAL_OPTS, SKU_STATUS_OPTS } from './selectOptions';

// SKU 库导出弹窗 (2026-10-13 改为服务端导出): Excel / CSV, 可选带二维码贴纸 (结果为 zip: 表格 + qr/ 目录).
// 筛选与列表页同一套后端语义, 打开时预填当前页面筛选, 可再调整; 列表有勾选时可只导勾选行.
// 二维码不逐个手填: 只选 尺寸 + 名称来源 (材料名 / SKU 商品名 二选一), 按每个 SKU 的主绑定材料出码,
// 与单张「生成二维码」同一套码; 未绑定材料的 SKU 在表格里标注无法生成.

export interface SkuExportFilter {
    /** 形态 single / bundle (SKU 库当前 tab) */
    kind?: SkuKind;
    keyword: string;
    xcode: string;
    prop: string;
    status: string;
    /** yes = 已绑定材料 / no = 未绑定 */
    hasMaterial: string;
    /** 绑定材料一级/二级类别编码 (categoryCode) / 工艺类型 capKey; 列表页不筛, 只在导出弹窗里可选 */
    type1?: string;
    type2?: string;
    capKey?: string;
}

const toQuery = (f: SkuExportFilter) => ({
    kind: f.kind,
    keyword: f.keyword.trim() || undefined,
    xcode: f.xcode.trim() || undefined,
    prop: f.prop || undefined,
    status: f.status || undefined,
    hasMaterial: f.hasMaterial || undefined,
    type1: f.type1 || undefined,
    type2: f.type2 || undefined,
    capKey: f.capKey || undefined,
});

function SkuExportModal({
    open,
    preset,
    selectedCodes,
    onClose,
}: {
    open: boolean;
    /** 打开时预填的筛选 (取自列表页当前条件) */
    preset: SkuExportFilter;
    /** 列表页当前勾选的 SKU 编码; 非空时可选"只导勾选" */
    selectedCodes: string[];
    onClose: () => void;
}) {
    const { capTypes } = useCapTypes();
    const categoryTree = useCategoryTree();
    const [f, setF] = useState<SkuExportFilter>(preset);
    const [onlySelected, setOnlySelected] = useState(false);
    const [format, setFormat] = useState<ExportSkusPayload['format']>('xlsx');
    const [withQr, setWithQr] = useState(false);
    const [qrSize, setQrSize] = useState<SkuQrSize>('dq003');
    const [nameSource, setNameSource] = useState<SkuQrNameSource>('material');
    const [hitCount, setHitCount] = useState<number | null>(null);
    const [busy, setBusy] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [error, setError] = useState('');

    // 打开时同步预填 (open 变化触发)
    useEffect(() => {
        if (open) {
            setF(preset);
            setOnlySelected(selectedCodes.length > 0);
            setError('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // 命中数预览 (pageSize=1 只取 totalCount), 输入防抖
    useEffect(() => {
        if (!open) return;
        if (onlySelected) {
            setHitCount(selectedCodes.length);
            return;
        }
        let cancelled = false;
        setHitCount(null);
        const timer = window.setTimeout(() => {
            listSkus({ ...toQuery(f), page: 1, pageSize: 1 })
                .then((res) => !cancelled && setHitCount(res.totalCount))
                .catch(() => !cancelled && setHitCount(null));
        }, 300);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [open, f, onlySelected, selectedCodes.length]);

    // 生成耗时计时 (带二维码时可能要几分钟, 给个进度感)
    useEffect(() => {
        if (!busy) return;
        setElapsed(0);
        const timer = window.setInterval(() => setElapsed((s) => s + 1), 1000);
        return () => window.clearInterval(timer);
    }, [busy]);

    const onExport = async () => {
        setBusy(true);
        setError('');
        try {
            const payload: ExportSkusPayload = {
                ...(onlySelected ? { skuCodes: selectedCodes } : toQuery(f)),
                format,
                qr: withQr ? { size: qrSize, nameSource } : undefined,
            };
            const filename = withQr ? `skus-qr-${dateSuffix()}.zip` : `skus-${dateSuffix()}.${format}`;
            await exportSkus(payload, filename);
            onClose();
        } catch (e) {
            setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        } finally {
            setBusy(false);
        }
    };

    const set = (patch: Partial<SkuExportFilter>) => setF({ ...f, ...patch });
    const children = categoryTree.find((t) => t.categoryCode === f.type1)?.children || [];
    const radioCls = (active: boolean) =>
        `flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 text-sm transition ${
            active ? 'border-brand-500 bg-brand-50' : 'border-slate-200 hover:border-slate-300'
        }`;

    return (
        <Modal
            open={open}
            width={680}
            title="导出 SKU"
            subtitle="按筛选条件导出全部命中行; 可附带二维码贴纸"
            onClose={busy ? () => undefined : onClose}
        >
            <div className="space-y-5">
                {selectedCodes.length > 0 && (
                    <div className="flex gap-2">
                        <label className={radioCls(onlySelected)}>
                            <input type="radio" className="mt-0.5" checked={onlySelected} onChange={() => setOnlySelected(true)} />
                            只导出列表中勾选的 {selectedCodes.length} 个
                        </label>
                        <label className={radioCls(!onlySelected)}>
                            <input type="radio" className="mt-0.5" checked={!onlySelected} onChange={() => setOnlySelected(false)} />
                            按筛选条件导出
                        </label>
                    </div>
                )}

                {!onlySelected && (
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="field-label">关键词</label>
                            <input
                                className="inp"
                                placeholder="SKU编码 / 材质编码 / 产品名"
                                value={f.keyword}
                                onChange={(e) => set({ keyword: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="field-label">材质编码</label>
                            <input
                                className="inp"
                                placeholder="精确匹配, 例 PL002-01"
                                value={f.xcode}
                                onChange={(e) => set({ xcode: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="field-label">材料一级分类</label>
                            <SearchSelect
                                className="w-full"
                                value={f.type1 || ''}
                                onChange={(v) => set({ type1: v, type2: '' })}
                                placeholder="全部"
                                options={zhOptions(categoryTree, (t) => t.categoryCode, (t) => t.nameZh)}
                                clearable
                            />
                        </div>
                        <div>
                            <label className="field-label">材料二级分类</label>
                            <SearchSelect
                                className="w-full"
                                value={f.type2 || ''}
                                disabled={!f.type1}
                                onChange={(v) => set({ type2: v })}
                                placeholder={f.type1 ? '全部' : '先选一级分类'}
                                options={zhOptions(children, (c) => c.categoryCode, (c) => c.nameZh)}
                                clearable
                            />
                        </div>
                        <div>
                            <label className="field-label">适配工艺类型</label>
                            <SearchSelect
                                className="w-full"
                                value={f.capKey || ''}
                                onChange={(v) => set({ capKey: v })}
                                placeholder="全部"
                                options={capTypes.map((c) => ({ value: c.capKey, label: c.label }))}
                                clearable
                            />
                        </div>
                        <div>
                            <label className="field-label">属性</label>
                            <SkuPropFilter className="sel" allLabel="全部" value={f.prop} onChange={(v) => set({ prop: v })} />
                        </div>
                        <div>
                            <label className="field-label">状态</label>
                            <SearchSelect
                                className="w-full"
                                value={f.status}
                                onChange={(v) => set({ status: v })}
                                placeholder="全部"
                                options={SKU_STATUS_OPTS}
                                clearable
                            />
                        </div>
                        <div>
                            <label className="field-label">绑定材料</label>
                            <SearchSelect
                                className="w-full"
                                value={f.hasMaterial}
                                onChange={(v) => set({ hasMaterial: v })}
                                placeholder="全部"
                                options={SKU_HAS_MATERIAL_OPTS}
                                clearable
                            />
                        </div>
                    </div>
                )}

                <div className="space-y-3 border-t border-slate-100 pt-4">
                    <div className="flex items-center gap-3">
                        <div className="w-20 shrink-0 text-sm font-semibold">文件格式</div>
                        <div className="flex gap-2">
                            <label className={radioCls(format === 'xlsx')}>
                                <input type="radio" className="mt-0.5" checked={format === 'xlsx'} onChange={() => setFormat('xlsx')} />
                                Excel (.xlsx)
                            </label>
                            <label className={radioCls(format === 'csv')}>
                                <input type="radio" className="mt-0.5" checked={format === 'csv'} onChange={() => setFormat('csv')} />
                                CSV
                            </label>
                        </div>
                    </div>
                    <label className="flex cursor-pointer items-center gap-2 text-sm">
                        <input type="checkbox" checked={withQr} onChange={(e) => setWithQr(e.target.checked)} />
                        <span className="font-semibold">附带二维码贴纸</span>
                        <span className="text-xs text-slate-400">结果为 zip: 表格 + qr/ 目录下的 PNG (300 DPI)</span>
                    </label>
                    {withQr && (
                        <div className="space-y-3 rounded-lg bg-slate-50 p-3">
                            <div className="flex items-start gap-3">
                                <div className="w-20 shrink-0 pt-2 text-sm font-semibold">尺寸</div>
                                <div className="grid flex-1 grid-cols-2 gap-2">
                                    {SKU_QR_SIZE_OPTIONS.map((o) => (
                                        <label key={o.key} className={radioCls(qrSize === o.key)}>
                                            <input
                                                type="radio"
                                                className="mt-0.5"
                                                checked={qrSize === o.key}
                                                onChange={() => setQrSize(o.key)}
                                            />
                                            <div>
                                                <div className="font-medium">{o.label}</div>
                                                <div className="text-xs text-slate-500">
                                                    {o.desc} · {o.width}x{o.height}
                                                </div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="w-20 shrink-0 pt-2 text-sm font-semibold">名称来源</div>
                                <div className="grid flex-1 grid-cols-2 gap-2">
                                    <label className={radioCls(nameSource === 'material')}>
                                        <input
                                            type="radio"
                                            className="mt-0.5"
                                            checked={nameSource === 'material'}
                                            onChange={() => setNameSource('material')}
                                        />
                                        <div>
                                            <div className="font-medium">材料名</div>
                                            <div className="text-xs text-slate-500">主绑定材料的英文名; 同材料的 SKU 共用一张</div>
                                        </div>
                                    </label>
                                    <label className={radioCls(nameSource === 'product')}>
                                        <input
                                            type="radio"
                                            className="mt-0.5"
                                            checked={nameSource === 'product'}
                                            onChange={() => setNameSource('product')}
                                        />
                                        <div>
                                            <div className="font-medium">SKU 商品名</div>
                                            <div className="text-xs text-slate-500">SKU 的英文商品名</div>
                                        </div>
                                    </label>
                                </div>
                            </div>
                            <div className="text-[11px] leading-5 text-slate-500">
                                每个 SKU 按主绑定材料出码 (与单张「生成二维码」同一套码), 正文为「材质编码: 名称」;
                                无英文名时取中文名, 所选来源为空时退回另一来源; 未绑定材料的 SKU 在表格中标注无法生成。
                                张数较多时需要几分钟, 请保持页面打开。
                            </div>
                        </div>
                    )}
                </div>

                {error && <div className="text-sm text-rose-600">{error}</div>}
                <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
                    <div className="text-xs text-slate-500">
                        {hitCount === null ? '统计中…' : `将导出 ${hitCount} 个 SKU`}
                    </div>
                    <div className="flex gap-2">
                        <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>
                            取消
                        </button>
                        <button type="button" className="btn-primary" onClick={onExport} disabled={busy || hitCount === 0}>
                            {busy ? `生成中… ${elapsed}s` : withQr ? '导出 (含二维码)' : format === 'xlsx' ? '导出 Excel' : '导出 CSV'}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

export default SkuExportModal;
